import { Box, Card, NavList } from '@deckee/deck-hand';
import { calculateDistanceBetweenTwoPointsSphericalLawOfCosines } from '@deckee/utilities';
import React, { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../../services/event-capture';
import { AppState, useAppDispatch } from '../../../../../services/store';
import { selectCurrentMapCenter } from '../../../../../services/store/modules/app-controls';
import { fetchPartners } from '../../../../../services/store/modules/partners';
import { Partner } from '../../../../../services/store/modules/partners/partners.models';
import { getAllPartners } from '../../../../../services/store/modules/partners/partners.selectors';
import { PartnerItemMarkerConfigJson } from '@deckee/api-app-main';

const StyledPartnerIcon = styled.img`
  max-height: 50px;
  max-width: 40px;
  margin-right: 6px;
`;

interface MapCenter {
  lat: number;
  lon: number;
}

interface PartnerWithMarker extends Omit<Partner, 'markerConfigJson'> {
  markerConfigJson: PartnerItemMarkerConfigJson;
}

/**
 * Sort partners by distance from map center.
 * Partners with markers are sorted by proximity (closest first).
 * Partners without markers maintain their original order at the end.
 */
export const sortPartnersByDistance = (
  partners: Partner[],
  mapCenter?: MapCenter | null,
): Partner[] => {
  if (!partners || partners.length === 0) return [];

  const partnersWithMarker: PartnerWithMarker[] = [];
  const partnersWithoutMarker: Partner[] = [];

  partners.forEach((partner) => {
    const hasMarkerCoordinates =
      partner.markerConfigJson?.latitude != null &&
      partner.markerConfigJson?.longitude != null &&
      partner.markerConfigJson.latitude !== '' &&
      partner.markerConfigJson.longitude !== '';

    if (hasMarkerCoordinates) {
      partnersWithMarker.push(partner as PartnerWithMarker);
    } else {
      partnersWithoutMarker.push(partner);
    }
  });

  // Sort partners with markers by distance from map center
  if (mapCenter?.lat != null && mapCenter?.lon != null) {
    partnersWithMarker.sort((a, b) => {
      const distanceA =
        calculateDistanceBetweenTwoPointsSphericalLawOfCosines(
          mapCenter.lat,
          mapCenter.lon,
          a.markerConfigJson.latitude,
          a.markerConfigJson.longitude,
        ) ?? Infinity;
      const distanceB =
        calculateDistanceBetweenTwoPointsSphericalLawOfCosines(
          mapCenter.lat,
          mapCenter.lon,
          b.markerConfigJson.latitude,
          b.markerConfigJson.longitude,
        ) ?? Infinity;
      return distanceA - distanceB;
    });
  }

  return [...partnersWithMarker, ...partnersWithoutMarker];
};

const PartnersCard: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const bounds = useSelector(
    (state: AppState) =>
      state.appControls.currentMapParams.viewPortDetails.bounds,
  );
  const mapCenter = useSelector(selectCurrentMapCenter);
  const partners = useSelector(getAllPartners);

  useEffect(() => {
    const promise = dispatch(fetchPartners());

    return () => {
      promise.abort();
    };
  }, [dispatch, bounds]);

  const handleSelectPartner = (partner: Partner) => {
    captureEvent({
      type: 'prepare: click partner',
      payload: {
        partnerId: partner.id,
        partnerName: partner.name,
      },
    });

    history.push(`/maps/partner/${partner.slug}`);
  };

  const sortedPartners = useMemo(
    () => sortPartnersByDistance(partners, mapCenter),
    [partners, mapCenter],
  );

  if (sortedPartners.length === 0) {
    return null;
  }

  return (
    <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
      <Card.Heading>Partners and resources</Card.Heading>

      <Card.Body>
        <NavList edge grouped>
          {sortedPartners.map((partner) => (
            <NavList.Item
              chevron
              key={partner.id}
              onClick={() => handleSelectPartner(partner)}
            >
              {partner.icon && <StyledPartnerIcon alt="" src={partner.icon} />}
              <Box>{partner.name}</Box>
            </NavList.Item>
          ))}
        </NavList>
      </Card.Body>
    </Card>
  );
};

export default PartnersCard;
